import { createAction } from '@reduxjs/toolkit'
import isEqual from 'lodash/isEqual'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'

import drawConflictByType from './drawConflictByType'
import { drawOverlappingDrawableLocationsModifications } from './highlightOptionConflicts'
import { DocumentMarkupStoreState, OverlayMarkupTools } from './insertEstimatedOptionOverlay'
import { selectMarkupState, sharedMarkupOperations } from './markupDocument'
import { DocumentMapping } from '../../../models/documentMapping'
import { IMaterialModificationConflict } from '../../../models/masterSetPlan'
import { DRAWING_TYPES } from '../../../shared/constants/drawable-types'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import {
    Color,
    Comment,
    Flag,
    Highlight,
    Image,
    Label,
    Overlay,
    PathTool,
    RegionTool,
    Workspace,
} from '../../lib/toolBoxes/2D'
import addSelectFunctionalityToModifications from '../../lib/utils/functionality-bindings/addSelectFunctionalityToModifications'
import {
    DEFAULT_OVERLAY_COLOR,
    selectCurrentOverlayChunkIds,
    selectFocusedEstimatedOptionOverlayChunkId,
    selectOpacityDict,
    selectOverlayColorDict,
} from '../../slices/2D'
import {
    NormalizedDocumentChunk,
    selectActiveFloorDocIdIgnoringOverlay,
    selectDocumentMappings,
    selectNormalizedDocumentChunks,
    updateActiveFloorDocId,
} from '../../slices/documents'
import { selectMaterialModificationConflicts } from '../../slices/masterSetPlan'
import { Vector2D } from '../../types'

export const pageChangeDuringConflictResolution = createAction<number>('pageChangeDuringConflictResolution')

export function* handlePageChangeDuringConflictResolution({
    payload,
}: ReturnType<typeof pageChangeDuringConflictResolution>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    // get tools from manager
    const [
        workspaceTool,
        overlayTool,
        regionTool,
        colorTool,
        commentTool,
        highlightTool,
        imageTool,
        flagTool,
        labelTool,
        pathTool,
    ]: OverlayMarkupTools = yield call(manager.getTools, [
        Workspace.NAME,
        Overlay.NAME,
        RegionTool.NAME,
        Color.NAME,
        Comment.NAME,
        Highlight.NAME,
        Image.NAME,
        Flag.NAME,
        Label.NAME,
        PathTool.NAME,
    ])
    const { openingGroups, areaOpacity, lineOpacity, drawableLocations, flags, toolObjects }: DocumentMarkupStoreState =
        yield select(selectMarkupState)

    const documentMappings: DocumentMapping[] | null = yield select(selectDocumentMappings)
    const normalizedDocumentChunks: NormalizedDocumentChunk[] | null = yield select(selectNormalizedDocumentChunks)

    const overlayChunkIds: number[] = yield select(selectCurrentOverlayChunkIds)

    const currentEstimatedChunkId: number | null = yield select(selectFocusedEstimatedOptionOverlayChunkId)

    const activeFloorDocId: number | null = yield select(selectActiveFloorDocIdIgnoringOverlay)

    if (
        !overlayChunkIds.length ||
        currentEstimatedChunkId ||
        !activeFloorDocId ||
        !documentMappings ||
        !normalizedDocumentChunks
    ) {
        return
    }

    // The active floor is the same as the one selected so do nothing
    if (payload === activeFloorDocId) return

    // All locked overlays share the same base home page, so checking the first is enough.
    const overlayDocumentMappings = overlayChunkIds
        .map((id) => documentMappings.find((dm) => dm.document_chunk_id === id))
        .filter((mapping): mapping is DocumentMapping => Boolean(mapping))
    const selectedDocumentMapping = documentMappings.find((dm) => dm.document_chunk_id === payload)
    const selectedDocumentChunk = normalizedDocumentChunks.find((chunk) => chunk.id === payload)

    if (
        overlayDocumentMappings.length !== overlayChunkIds.length ||
        !selectedDocumentMapping ||
        !selectedDocumentChunk
    ) {
        return
    }

    if (overlayDocumentMappings[0].additional_data.estimatedBaseHomeId === selectedDocumentMapping.id) {
        const overlayChunks = overlayChunkIds.map((id) => normalizedDocumentChunks.find((chunk) => chunk.id === id))
        const chunkOpacityDict: Record<number, number> = yield select(selectOpacityDict)
        const overlayColorDict: Record<number, string> = yield select(selectOverlayColorDict)
        const {
            materialModificationConflictsToSave,
            materialModificationConflicts,
        }: ReturnType<typeof selectMaterialModificationConflicts> = yield select(selectMaterialModificationConflicts)

        if (overlayChunks.some((chunk) => !chunk)) return

        yield put(updateActiveFloorDocId(payload))
        yield call(workspaceTool.clear)

        yield sharedMarkupOperations(
            {
                workspaceTool,
                imageTool,
                flagTool,
                colorTool,
                pathTool,
                labelTool,
                regionTool,
                highlightTool,
                commentTool,
            },
            selectedDocumentChunk,
            [],
            flags,
            toolObjects,
            drawableLocations,
            openingGroups,
            lineOpacity,
            areaOpacity
        )

        // Restore every locked overlay, not just the most-recently-added one.
        for (const [index, overlayChunkId] of overlayChunkIds.entries()) {
            const overlayChunk = overlayChunks[index] as NormalizedDocumentChunk
            const overlayDocumentMapping = overlayDocumentMappings[index]

            const chunkOpacity: number = isFinite(chunkOpacityDict[overlayChunkId])
                ? chunkOpacityDict[overlayChunkId] / 100
                : Overlay.DEFAULT_OPACITY
            const overlayColor: string = overlayColorDict[overlayChunkId] ?? DEFAULT_OVERLAY_COLOR

            yield call(overlayTool.insertOverlay, overlayChunk.src, overlayChunkId, chunkOpacity, overlayColor)
            yield call(overlayTool.updateOverlayScale, overlayChunkId, overlayDocumentMapping.overlay_scale ?? 1)
            yield call(
                overlayTool.setOverlayPositionCoordinates,
                overlayChunkId,
                overlayDocumentMapping.overlay_center as Vector2D
            )
        }

        yield call(overlayTool.lockOverlay, true)

        const materialModificationConflictsToRedraw = materialModificationConflicts.reduce(
            (cons, matCon) => {
                const savedConflict = materialModificationConflictsToSave.find(
                    (con) => con.material_location_id === matCon.material_location_id
                )

                const location = drawableLocations.find(
                    (loc) => loc.opening_location_id === matCon.material_location_id
                )

                if (location) {
                    if (savedConflict && !isEqual(savedConflict.coordinates, matCon.coordinates)) {
                        cons.push({
                            ...savedConflict,
                            shapeType: location.shapeType ?? DRAWING_TYPES.SECTION,
                            opening_group_id: location.opening_group_id,
                        })
                    } else if (!savedConflict) {
                        cons.push({
                            ...matCon,
                            shapeType: location.shapeType ?? DRAWING_TYPES.SECTION,
                            opening_group_id: location.opening_group_id,
                        })
                    }
                }

                return cons
            },
            [] as (IMaterialModificationConflict & {
                shapeType: DRAWING_TYPES
                opening_group_id: number
            })[]
        )

        yield all(
            materialModificationConflictsToRedraw.map((materialConflict) => {
                return call(
                    drawConflictByType,
                    materialConflict,
                    areaOpacity,
                    lineOpacity,
                    [],
                    addSelectFunctionalityToModifications
                )
            })
        )
    } else {
        // A page that was not the page the base home id, move to that page and set all
        // material to potential conflicts
        yield call(overlayTool.resetOverlayRasters)
        yield put(updateActiveFloorDocId(payload))
        yield call(workspaceTool.clear)
        yield call(imageTool.insertImage, selectedDocumentChunk.src)

        const pageLocations = drawableLocations.filter((loc) => loc.document_chunk_id === payload)

        // Shared across every locked overlay so a location conflicting with 2+ merges into one entry.
        const alreadyDrawnMaterialLocations = new Map<number, paper.Path>()

        for (const overlayChunkId of overlayChunkIds) {
            yield call(
                drawOverlappingDrawableLocationsModifications,
                pageLocations,
                [overlayChunkId],
                areaOpacity,
                lineOpacity,
                [],
                alreadyDrawnMaterialLocations
            )
        }
    }
}

export function* watchForPageChangeDuringConflictResolution() {
    yield takeEvery(pageChangeDuringConflictResolution.type, handlePageChangeDuringConflictResolution)
}
